import { createTheme } from "@mui/material/styles";
import { Calendar } from "@solar-icons/react/ssr";
import { createGlobalStyle } from "styled-components";

export const GlobalStyle = createGlobalStyle`
    body {
        margin: 0px;
        padding: 0px;
        color: ${(props) => (props.theme == "dark" ? colorPalette.white : colorPalette.black)};
        background: ${(props) => (props.theme == "dark" ? colorPalette.black : colorPalette.white)};
        font-family: 'Inter Tight', sans-serif;
    }

    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
  
    input[type="number"] {
        -moz-appearance: textfield;
    }
`;

export const colorPalette = {
    white: "#FAFAFA",
    black: "#181818",
    universalGrey: "#8A8A8A",
    green: {
        light: {
            main: "#27C77E",
            one: "#52D298",
            two: "#7DDDB2",
            three: "#A9E8CB",
        },
        dark: {
            main: "#27C77E",
            one: "#21A468",
            two: "#1A754C",
            three: "#0C4129",
        },
    },
    blue: {
        light: {
            main: "#0091FF",
            one: "#33A7FF",
            two: "#66BDFF",
            three: "#B4DFFF",
        },
        dark: {
            main: "#0091FF",
            one: "#0082E4",
            two: "#015A9D",
            three: "#002E50",
        },
    },
    orange: {
        light: {
            main: "#FFAC30",
            one: "#FFBD59",
            two: "#FFCD83",
            three: "#FFE0B2",
        },
        dark: {
            main: "#FFAC30",
            one: "#D08D28",
            two: "#9F6A1C",
            three: "#533911",
        },
    },
    red: {
        light: {
            main: "#E34336",
            one: "#E9695E",
            two: "#EE8E86",
            three: "#FDD4D0",
        },
        dark: {
            main: "#E34336",
            one: "#BB3327",
            two: "#96271E",
            three: "#731D16",
        },
    },
    grey: {
        light: {
            one: "#C9C9C9",
            two: "#D9D9D9",
            three: "#F2F2F2",
        },
        dark: {
            one: "#464646",
            two: "#363636",
            three: "#232323",
        },
    },
};

export const baseTheme = createTheme({
    palette: {
        primary: {
            main: colorPalette.green.light.main,
            light: colorPalette.green.light.one,
            dark: colorPalette.green.dark.one,
            contrastText: colorPalette.black,
        },
        secondary: {
            main: colorPalette.grey.light.two,
            light: colorPalette.grey.light.three,
            dark: colorPalette.grey.dark.one,
            contrastText: colorPalette.black,
        },
        info: {
            main: colorPalette.blue.light.main,
            light: colorPalette.blue.light.one,
            dark: colorPalette.blue.dark.one,
        },
        success: {
            main: colorPalette.green.light.main,
            light: colorPalette.green.light.one,
            dark: colorPalette.green.dark.one,
        },
        warning: {
            main: colorPalette.orange.light.main,
            light: colorPalette.orange.light.one,
            dark: colorPalette.orange.dark.one,
        },
        error: {
            main: colorPalette.red.light.main,
            light: colorPalette.red.light.one,
            dark: colorPalette.red.dark.one,
        },
        text: {
            primary: colorPalette.black,
            secondary: colorPalette.universalGrey,
            disabled: colorPalette.grey.light.one,
        },
        background: {
            default: "#FFFFFF",
            paper: "#F2F2F2",
            rainbow: `linear-gradient(90deg, #0192FF 0%, #43CF8F 100%)`,
        },
    },
    typography: {
        fontFamily: "Inter Tight, sans-serif",
        fontWeightMedium: 400,
        fontWeightBold: 600,
        fontWeightRegular: 400,
        fontWeightLight: 400,
        h1: {
            fontSize: 22,
            fontWeight: 600,
        },
        h2: {
            fontSize: 16,
            fontWeight: 600,
        },
        h3: {
            fontSize: 14,
            fontWeight: 600,
        },
        body1: {
            fontSize: 14,
            fontWeight: 400,
        },
        body2: {
            fontSize: 12,
            fontWeight: 400,
        },
        subtitle2: {
            fontSize: 10,
            fontWeight: 400,
        },
        button: {
            textTransform: "none",
            fontSize: 12,
            fontWeight: 400,
        },
    },
    transitions: {
        createMarginOpen: (theme) =>
            theme.transitions.create("margin", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        createMarginClose: (theme) =>
            theme.transitions.create("margin", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
    },
    components: {
        MuiDatePicker: {
            defaultProps: {
                components: { OpenPickerIcon: Calendar },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backdropFilter: "blur(4px)",
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    border: "none",
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 100,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": {
                        width: 14,
                        height: 14,
                    },
                    variants: [
                        {
                            props: { color: "primary", variant: "contained" },
                            style: {
                                backgroundColor: colorPalette.green.light.main,
                                color: colorPalette.black,
                                "&:hover": {
                                    backgroundColor:
                                        colorPalette.green.light.two,
                                },
                                "&:disabled": {
                                    backgroundColor:
                                        colorPalette.green.light.three,
                                    color: colorPalette.universalGrey,
                                },
                            },
                        },
                        {
                            props: { color: "error", variant: "contained" },
                            style: {
                                backgroundColor: colorPalette.red.light.one,
                                color: colorPalette.black,
                                "&:hover": {
                                    backgroundColor:
                                        colorPalette.red.light.three,
                                },
                                "&:disabled": {
                                    color: colorPalette.grey.light.one,
                                },
                            },
                        },
                        {
                            props: { color: "secondary", variant: "contained" },
                            style: {
                                backgroundColor: colorPalette.grey.light.three,
                                color: colorPalette.black,
                                "&:hover": {
                                    backgroundColor:
                                        colorPalette.grey.light.two,
                                },
                                "&:disabled": {
                                    backgroundColor:
                                        colorPalette.grey.light.three,
                                    color: colorPalette.grey.light.one,
                                },
                            },
                        },
                    ],
                    "&.MuiButton-sizeMedium": {
                        height: 30,
                        padding: "0 12px",
                    },
                    "&.MuiButton-sizeSmall": {
                        height: 24,
                        padding: "0 10px",
                        fontSize: 12,
                        "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg":
                            {
                                width: 12,
                                height: 12,
                            },
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    color: colorPalette.universalGrey,
                },
                body: {
                    fontSize: 14,
                },
            },
        },
        MuiChip: {
            defaultProps: {
                variant: "outlined",
            },
            styleOverrides: {
                root: {
                    fontSize: 12,
                    "&.MuiChip-root.MuiChip-clickable:hover": {
                        backgroundColor: "#9E9E9E",
                    },
                },
            },
            variants: [
                {
                    props: { color: "secondary" },
                    style: {
                        backgroundColor: colorPalette.grey.light.three,
                        borderColor: colorPalette.grey.dark.one,
                        color: colorPalette.grey.dark.one,
                    },
                },
                {
                    props: { color: "info" },
                    style: {
                        backgroundColor: colorPalette.blue.light.three,
                        borderColor: colorPalette.blue.light.main,
                        color: colorPalette.blue.light.main,
                    },
                },
                {
                    props: { color: "success" },
                    style: {
                        backgroundColor: colorPalette.green.light.three,
                        borderColor: colorPalette.green.dark.two,
                        color: colorPalette.green.dark.two,
                    },
                },
                {
                    props: { color: "warning" },
                    style: {
                        backgroundColor: colorPalette.orange.light.three,
                        borderColor: colorPalette.orange.dark.one,
                        color: colorPalette.orange.dark.one,
                    },
                },
                {
                    props: { color: "error" },
                    style: {
                        backgroundColor: colorPalette.red.light.three,
                        borderColor: colorPalette.red.light.main,
                        color: colorPalette.red.light.main,
                    },
                },
            ],
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                },
            },
        },
    },
});

export const darkTheme = createTheme({
    ...baseTheme,
    palette: {
        mode: "dark",
        primary: {
            main: colorPalette.green.dark.one,
            light: colorPalette.green.dark.main,
            dark: colorPalette.green.dark.two,
            contrastText: colorPalette.green.dark.main,
        },
        secondary: {
            main: colorPalette.grey.dark.two,
            light: colorPalette.grey.dark.one,
            dark: colorPalette.grey.dark.three,
            contrastText: colorPalette.white,
        },
        error: {
            main: colorPalette.red.dark.main,
            light: colorPalette.red.dark.one,
            dark: colorPalette.red.dark.two,
            contrastText: colorPalette.red.dark.main,
        },
        warning: {
            main: colorPalette.orange.dark.one,
            light: colorPalette.orange.dark.main,
            dark: colorPalette.orange.dark.two,
            contrastText: colorPalette.orange.dark.main,
        },
        info: {
            main: colorPalette.blue.dark.one,
            light: colorPalette.blue.dark.main,
            dark: colorPalette.blue.dark.two,
            contrastText: colorPalette.blue.dark.main,
        },
        success: {
            main: colorPalette.green.dark.one,
            light: colorPalette.green.dark.main,
            dark: colorPalette.green.dark.two,
            contrastText: colorPalette.green.dark.main,
        },
        text: {
            primary: colorPalette.white,
            secondary: colorPalette.universalGrey,
            disabled: colorPalette.grey.dark.one,
        },
        action: {
            active: "#FFFFFF",
            hover: "rgba(255, 255, 255, 0.08)",
            selected: "rgba(255, 255, 255, 0.16)",
            disabled: "#9E9E9E",
            disabledBackground: "rgba(255, 255, 255, 0.12)",
        },
        background: {
            ...baseTheme.palette.background,
            default: colorPalette.black,
            paper: colorPalette.grey.dark.three,
        },
        divider: "#F2F2F2",
    },
    components: {
        ...baseTheme.components,
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: "#232323",
                    backgroundImage: "none",
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                option: {
                    "&.MuiAutocomplete-option.Mui-focused": {
                        backgroundColor: "#9E9E9E",
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    ".MuiMenuItem-root:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                },
            },
        },
        MuiChip: {
            defaultProps: {
                variant: "outlined",
            },
            styleOverrides: {
                root: {
                    fontSize: 12,
                },
            },
            variants: [
                {
                    props: { color: "secondary" },
                    style: {
                        backgroundColor: colorPalette.grey.dark.three,
                        borderColor: colorPalette.grey.light.one,
                        color: colorPalette.grey.light.one,
                    },
                },
                {
                    props: { color: "info" },
                    style: {
                        backgroundColor: colorPalette.blue.dark.three,
                        borderColor: colorPalette.blue.light.one,
                        color: colorPalette.blue.light.one,
                    },
                },
                {
                    props: { color: "success" },
                    style: {
                        backgroundColor: colorPalette.green.dark.three,
                        borderColor: colorPalette.green.light.main,
                        color: colorPalette.green.light.one,
                    },
                },
                {
                    props: { color: "warning" },
                    style: {
                        backgroundColor: colorPalette.orange.dark.three,
                        borderColor: colorPalette.orange.light.main,
                        color: colorPalette.orange.light.main,
                    },
                },
                {
                    props: { color: "error" },
                    style: {
                        backgroundColor: colorPalette.red.dark.three,
                        borderColor: colorPalette.red.light.one,
                        color: colorPalette.red.light.one,
                    },
                },
            ],
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: colorPalette.grey.dark.one,
                },
            },
        },
        MuiButton: {
            ...baseTheme.components.MuiButton,
            styleOverrides: {
                root: {
                    ...baseTheme.components.MuiButton.styleOverrides.root,
                    variants: [
                        {
                            props: { color: "primary", variant: "contained" },
                            style: {
                                backgroundColor: colorPalette.green.dark.one,
                                color: colorPalette.white,
                                "&:hover": {
                                    backgroundColor:
                                        colorPalette.green.dark.two,
                                },
                                "&:disabled": {
                                    backgroundColor:
                                        colorPalette.green.dark.two,
                                    color: colorPalette.universalGrey,
                                },
                            },
                        },
                        {
                            props: { color: "error", variant: "contained" },
                            style: {
                                backgroundColor: colorPalette.red.dark.one,
                                color: colorPalette.white,
                                "&:hover": {
                                    backgroundColor:
                                        colorPalette.red.dark.three,
                                },
                                "&:disabled": {
                                    color: colorPalette.grey.dark.one,
                                },
                            },
                        },
                        {
                            props: { color: "secondary", variant: "contained" },
                            style: {
                                backgroundColor: colorPalette.grey.dark.two,
                                color: colorPalette.white,
                                "&:hover": {
                                    backgroundColor: colorPalette.grey.dark.one,
                                },
                                "&:disabled": {
                                    backgroundColor:
                                        colorPalette.grey.dark.three,
                                    color: colorPalette.universalGrey,
                                },
                            },
                        },
                        {
                            props: { color: "info", variant: "text" },
                            style: {
                                color: colorPalette.white,
                                "&:hover": {
                                    backgroundColor: "transparent",
                                    color: colorPalette.green.dark.one,
                                },
                                "&:disabled": {
                                    color: colorPalette.green.dark.two,
                                },
                            },
                        },
                    ],
                },
            },
        },
    },
});
